import { type FC, useCallback, useMemo, useState } from "react";
import { Button } from "../../components/Button";
import { Card } from "../../components/Card";
import { Stepper, type StepSettings } from "../../components/Stepper";
import { AppPage } from "../../layout/AppLayout";
import { FormGenerateOverview } from "./FormGenerateOverview";
import { FormOverview } from "./FormOverview";
import { OnboardProvider, useOnboardContext } from "./Onboard";
import "./PageOnboard.css";
import { Alert, Col, Row } from "react-bootstrap";
import { useEventCallback } from "usehooks-ts";
import { AlertType, useAlertEmit } from "../../common/alert/Alert";
import { Icon } from "../../common/Icon";
import { useModal } from "../../common/modal/Modal";
import { useCompany } from "../../company/useCompany";
import { IcpEditor } from "./IcpEditor";
import { ModalGoal } from "./ModalGoal";
import { useOnboarding } from "./useOnboarding";

const steps: Array<StepSettings> = [
	{
		title: "Business Overview",
		icon: "error",
		substeps: ["overview", "saveOverview"],
		currentSubStep: 0,
	},
	{
		title: "Ideal Customer Profile",
		icon: "error",
		substeps: ["idealCustomer", "saveIdealCustomer"],
		currentSubStep: 0,
	},
	{
		title: "Marketing Plan",
		icon: "error",
	},
];

const StepOverview: FC = () => {
	const { currentSubStep, completeCurrentStep, nextStep, nextSubstep } =
		useOnboardContext();
	const [hasWebsite, setHasWebsite] = useState<boolean>(true);

	const onOverviewSuccess = useCallback(() => {
		completeCurrentStep();
		nextStep();
	}, [completeCurrentStep, nextStep]);

	return (
		<Card className="card-onboard">
			<Card.Body>
				{hasWebsite && currentSubStep === "overview" && (
					<>
						<FormGenerateOverview onSuccess={nextSubstep} />

						<Button
							variant="link"
							className="btn btn-link"
							type="button"
							onClick={() => setHasWebsite(false)}
						>
							I do not have a website
						</Button>
					</>
				)}
				{!hasWebsite && currentSubStep === "overview" && (
					<>
						<p>
							Since you don’t have a website yet, we’ll need a quick overview to
							get started. This helps us understand your brand, your goals, and
							the kind of customers you're trying to reach, so we can start
							building a personalized marketing plan that fits your needs.
						</p>

						<FormOverview
							onSuccess={onOverviewSuccess}
							hasWebsite={hasWebsite}
							setHasWebsite={setHasWebsite}
						/>
					</>
				)}

				{currentSubStep === "saveOverview" && (
					<>
						<p>
							Your business description and Marketing Voice help us understand
							both who your customer is likely to be and how you want to be
							perceived by your customer. We use this as we create your content
							to ensure that it resonates for your customers.{" "}
						</p>

						<Alert variant="warning">
							<Icon name="warning" size={24} />{" "}
							<strong>
								Review the information and make any changes that you would like.
								Then we will move on to the next step.
							</strong>
						</Alert>

						<FormOverview
							onSuccess={onOverviewSuccess}
							hasWebsite={hasWebsite}
							setHasWebsite={setHasWebsite}
						/>
					</>
				)}
			</Card.Body>
		</Card>
	);
};

const StepIdealCustomer: FC = () => {
	const { currentSubStep, prevStep, nextSubstep } = useOnboardContext();
	const { idealCustomerProfile } = useOnboarding();
	const company = useCompany();
	const emit = useAlertEmit();
	const { mutateAsync, isPending } = idealCustomerProfile;
	const { open } = useModal();

	const icps = useMemo(() => company.icps, [company]);
	const hasApprovedIcp = useMemo(
		() => icps.some((icp) => icp.approved),
		[icps],
	);

	const generateICP = useCallback(async () => {
		const data = await mutateAsync(undefined, {
			onSuccess: () => {
				emit({
					type: AlertType.SUCCESS,
					content: "Successfully generated ideal customer profiles",
				});
				nextSubstep();
			},
			onError: () => {
				emit({
					type: AlertType.ERROR,
					content: "Error when generating ideal customer profiles",
				});
			},
		});

		return data;
	}, [mutateAsync, nextSubstep, emit]);

	const handleOpen = useEventCallback(() => {
		open(ModalGoal, {});
	});

	return (
		<>
			{currentSubStep === "idealCustomer" && (
				<Card className="card-promt">
					<Card.Body>
						<p>
							Our next step is to define your ideal customer profile. This helps
							determine how to position your business and where to place your
							marketing to get the biggest return on your work. Click Create
							Ideal Customer Profile to proceed.
						</p>

						<div className="bar">
							<Button
								loading={isPending}
								onClick={async () => {
									await generateICP();
								}}
							>
								Create Ideal Customer Profile
							</Button>

							<Button variant="link" onClick={prevStep}>
								Back
							</Button>
						</div>
					</Card.Body>
				</Card>
			)}
			{currentSubStep === "saveIdealCustomer" && (
				<>
					<Card>
						<Card.Body>
							<p>
								<strong>Ok, we are almost there!</strong>
							</p>

							<p>
								Defining your customer in the detail that you see below helps us
								to build out a content plan that resonates with your customers
								and that they will want to do business with you.{" "}
							</p>

							<p>
								Also, if you choose to get into targeted ads, we use this
								information to optimize your budget by only showing ads to your
								core customers and prospects. Read through it, make sure that it
								is what you want and then approve the description. Your
								marketing plan is coming next.
							</p>
						</Card.Body>
					</Card>

					<Row className="row-onboard">
						{icps.map((icp) => (
							<Col md={6} key={`ICP-editor-${icp.id}-${icp.company_id}`}>
								<IcpEditor icp={icp} />
							</Col>
						))}
					</Row>

					{hasApprovedIcp && (
						<div className="bar-onboard">
							<p>
								We are now going to create your complete Marketing Plan. Hit the
								button below to get started.
							</p>

							<Button onClick={handleOpen}>Next</Button>
						</div>
					)}
				</>
			)}
		</>
	);
};

const PageOnboardConsumer: FC = () => {
	const { current, steps } = useOnboardContext();
	return (
		<>
			<Stepper current={current} steps={steps} iconSize={24} />

			{current === 0 && <StepOverview />}

			{current === 1 && <StepIdealCustomer />}
		</>
	);
};

export const PageOnboard: FC = () => {
	return (
		<AppPage title="Set Up Marketing Plan">
			<OnboardProvider steps={steps}>
				<PageOnboardConsumer />
			</OnboardProvider>
		</AppPage>
	);
};
